import { TrendingDown, TrendingUp } from "lucide-react";
import React, { useMemo } from "react";
import { BANK_ACCOUNTS } from "@/data/financial-systems";
import { useBaseForecast } from "@/hooks/useBaseForecast";
import { useFormatting } from "@/hooks/useFormatting";
import { useTerminology } from "@/hooks/useTerminology";

export const CashFlowStatementPreview: React.FC = () => {
  const { formatCurrency } = useFormatting();
  const { getCashMovementTerms } = useTerminology();
  const terms = getCashMovementTerms();
  const { baseForecast: forecastData } = useBaseForecast();

  // Build weekly summary rows from the 90-day forecast
  const weeks = useMemo(() => {
    const result: {
      label: string;
      inflows: number;
      outflows: number;
      net: number;
      closingBalance: number;
    }[] = [];

    for (let week = 0; week < 13; week++) {
      const startDay = week * 7;
      const endDay = Math.min(startDay + 6, 90);
      let inflows = 0;
      let outflows = 0;

      for (let d = startDay; d <= endDay; d++) {
        inflows += forecastData[d]?.incoming ?? 0;
        outflows += forecastData[d]?.outgoing ?? 0;
      }

      const closingBalance = forecastData[endDay]?.balance ?? 0;
      const weekStart = new Date(Date.now() + startDay * 24 * 60 * 60 * 1000);
      const weekEnd = new Date(Date.now() + endDay * 24 * 60 * 60 * 1000);

      result.push({
        label: `${weekStart.toLocaleDateString("en-GB", { day: "numeric", month: "short" })} – ${weekEnd.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`,
        inflows,
        outflows,
        net: inflows - outflows,
        closingBalance,
      });
    }

    return result;
  }, [forecastData]);

  const totalInflows = weeks.reduce((s, w) => s + w.inflows, 0);
  const totalOutflows = weeks.reduce((s, w) => s + w.outflows, 0);
  const totalNet = totalInflows - totalOutflows;
  const openingBalance =
    forecastData[0]?.balance ??
    BANK_ACCOUNTS.reduce((sum, a) => sum + a.balance, 0);
  const closingBalance = forecastData[90]?.balance ?? 0;

  return (
    <div className="space-y-6">
      {/* Summary strip */}
      <div className="grid grid-cols-3 gap-4">
        <div className="rounded-lg border bg-muted/40 p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">Opening Balance</p>
          <p className="text-xl font-bold">{formatCurrency(openingBalance)}</p>
        </div>
        <div
          className={`rounded-lg border p-4 text-center ${totalNet >= 0 ? "bg-green-50 border-green-200 dark:bg-green-950/30 dark:border-green-800" : "bg-red-50 border-red-200 dark:bg-red-950/30 dark:border-red-800"}`}
        >
          <p className="text-xs text-muted-foreground mb-1">
            Net Movement (90 days)
          </p>
          <p
            className={`text-xl font-bold ${totalNet >= 0 ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"}`}
          >
            {formatCurrency(Math.abs(totalNet))} {totalNet >= 0 ? "▲" : "▼"}
          </p>
        </div>
        <div className="rounded-lg border bg-muted/40 p-4 text-center">
          <p className="text-xs text-muted-foreground mb-1">Closing Balance</p>
          <p className="text-xl font-bold">{formatCurrency(closingBalance)}</p>
        </div>
      </div>

      {/* Weekly breakdown table */}
      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-muted/60">
              <th className="px-4 py-3 text-left font-semibold">Week</th>
              <th className="px-4 py-3 text-right font-semibold text-green-700 dark:text-green-400">
                {terms.in}
              </th>
              <th className="px-4 py-3 text-right font-semibold text-red-700 dark:text-red-400">
                {terms.out}
              </th>
              <th className="px-4 py-3 text-right font-semibold">Net</th>
              <th className="px-4 py-3 text-right font-semibold">
                Closing Balance
              </th>
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, i) => (
              <tr
                key={i}
                className="border-b hover:bg-muted/30 transition-colors"
              >
                <td className="px-4 py-2.5 text-muted-foreground">
                  {week.label}
                </td>
                <td className="px-4 py-2.5 text-right text-green-700 dark:text-green-400">
                  {formatCurrency(week.inflows)}
                </td>
                <td className="px-4 py-2.5 text-right text-red-700 dark:text-red-400">
                  {formatCurrency(week.outflows)}
                </td>
                <td className="px-4 py-2.5 text-right">
                  <span
                    className={`inline-flex items-center gap-1 ${week.net >= 0 ? "text-green-700 dark:text-green-400" : "text-red-700 dark:text-red-400"}`}
                  >
                    {week.net >= 0 ? (
                      <TrendingUp className="h-3 w-3" />
                    ) : (
                      <TrendingDown className="h-3 w-3" />
                    )}
                    {formatCurrency(Math.abs(week.net))}
                  </span>
                </td>
                <td className="px-4 py-2.5 text-right font-medium">
                  {formatCurrency(week.closingBalance)}
                </td>
              </tr>
            ))}
            {/* Totals row */}
            <tr className="border-t-2 bg-muted/60 font-semibold">
              <td className="px-4 py-3">Total (90 days)</td>
              <td className="px-4 py-3 text-right text-green-700 dark:text-green-400">
                {formatCurrency(totalInflows)}
              </td>
              <td className="px-4 py-3 text-right text-red-700 dark:text-red-400">
                {formatCurrency(totalOutflows)}
              </td>
              <td className="px-4 py-3 text-right">
                <span
                  className={
                    totalNet >= 0
                      ? "text-green-700 dark:text-green-400"
                      : "text-red-700 dark:text-red-400"
                  }
                >
                  {formatCurrency(Math.abs(totalNet))}{" "}
                  {totalNet >= 0 ? "▲" : "▼"}
                </span>
              </td>
              <td className="px-4 py-3 text-right">
                {formatCurrency(closingBalance)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};
